import * as http from "./http.js";
import jwtAuthService from "./jwtAuthService.js";
import { API_BASE_URL, AI_API_URL } from "./apiConfig.js";

// Export the API URLs from the centralized config
export { API_BASE_URL, AI_API_URL };

// Utility Functions
export const formatDate = (date) => {
    var d = new Date(date),
        month = '' + (d.getMonth() + 1),
        day = '' + d.getDate(),
        year = d.getFullYear();

    if (month.length < 2)
        month = '0' + month;
    if (day.length < 2)
        day = '0' + day;
    return [year, month, day].join('-');
};

export const getBaseUrl = (url) => {
    if (url.indexOf("?") !== -1) {
        url = url.substring(0, url.indexOf("?"));
    }
    return url;
};

// Authentication APIs - Updated for JWT
export const signupUser = async (userData) => {
    const api = `${API_BASE_URL}/signup/signup`;
    return http.post(api, userData).then(response => {
        return response;
    }).catch(error => {
        console.error('Signup API Error:', error);
        throw error;
    });
};

// Google OAuth Signup API
export const signupWithGoogle = async (googleUserData) => {
    const api = `${API_BASE_URL}/signup/google_signup`;
    return http.post(api, googleUserData).then(response => {
        return response;
    }).catch(error => {
        console.error('Google Signup API Error:', error);
        throw error;
    });
};

// Google OAuth Login API
export const loginWithGoogle = async (googleUserData) => {
    const api = `${API_BASE_URL}/login/google_login`;
    return http.post(api, {
        google_id: googleUserData.googleId,
        email: googleUserData.email,
        access_token: googleUserData.accessToken,
        device_info: googleUserData.deviceInfo || 'Web Browser'
    }).then(response => {
        return response;
    }).catch(error => {
        console.error('Google Login API Error:', error);
        throw error;
    });
};

export const loginUser = async (credentials) => {
    // Use JWT authentication service
    return jwtAuthService.login(credentials);
};

export const logoutUser = async () => {
    // Use JWT authentication service
    return jwtAuthService.logout();
};

// JWT specific authentication functions
export const jwtLogin = async (credentials) => {
    return jwtAuthService.login(credentials);
};

export const jwtLogout = async () => {
    return jwtAuthService.logout();
};

export const isAuthenticated = async () => {
    return jwtAuthService.isAuthenticated();
};

export const getCurrentUser = () => {
    return jwtAuthService.getCurrentUser();
};

// User Management APIs
export const getUserProfile = async (userId) => {
    const api = `${API_BASE_URL}/user/${userId}`;
    return http.get(api).then(response => {
        return response;
    }).catch(error => {
        console.error('Get User Profile API Error:', error);
        throw error;
    });
};

export const updateUserProfile = async (userData) => {
    const api = `${API_BASE_URL}/user/${userData.id}`;
    return http.put(api, userData).then(response => {
        return response;
    }).catch(error => {
        console.error('Update User Profile API Error:', error);
        throw error;
    });
};

export const changePassword = async (passwordData) => {
    const api = `${API_BASE_URL}/user/update_password`;
    return http.post(api, passwordData).then(response => {
        return response;
    }).catch(error => {
        console.error('Change Password API Error:', error);
        throw error;
    });
};

// User Profile Data APIs (for current logged-in user)
export const getMyProfile = async () => {
    const api = `${API_BASE_URL}/user/profile`;
    return http.get(api).then(response => {
        return response;
    }).catch(error => {
        console.error('Get My Profile API Error:', error);
        throw error;
    });
};

export const saveMyProfile = async (profileData) => {
    const api = `${API_BASE_URL}/user/profile`;
    return http.post(api, profileData).then(response => {
        return response;
    }).catch(error => {
        console.error('Save My Profile API Error:', error);
        throw error;
    });
};

// User Quota Information API
export const getUserQuota = async () => {
    const api = `${API_BASE_URL}/user/quota`;
    return http.get(api).then(response => {
        return response;
    }).catch(error => {
        console.error('Get User Quota API Error:', error);
        // Return a default quota structure if API fails
        return {
            status: false,
            result: {
                subscription_type: 'free',
                custom_quiz_count: 0,
                custom_quiz_limit: 20,
                remaining_quizzes: 20,
                quota_exceeded: false
            }
        };
    });
};

// User Subscription Features API
export const getUserSubscriptionFeatures = async () => {
    const api = `${API_BASE_URL}/user/subscription_features`;
    return http.get(api).then(response => {
        return response;
    }).catch(error => {
        console.error('Get User Subscription Features API Error:', error);
        throw error;
    });
};

export const getTimeSlots = async () => {
    const api = `${API_BASE_URL}/user/time-slots`;
    return http.get(api).then(response => {
        return response;
    }).catch(error => {
        console.error('Get Time Slots API Error:', error);
        throw error;
    });
};

// Subject APIs
export const getAllSubjects = async () => {
    const api = `${API_BASE_URL}/subject`;
    return http.get(api).then(response => {
        return response;
    }).catch(error => {
        console.error('Get Subjects API Error:', error);
        throw error;
    });
};

export const getSubject = async (subjectId) => {
    const api = `${API_BASE_URL}/subject/${subjectId}`;
    return http.get(api).then(response => {
        return response;
    }).catch(error => {
        console.error('Get Subject API Error:', error);
        throw error;
    });
};

// Chapter APIs
export const getAllChapters = async (filters = {}) => {
    let api = `${API_BASE_URL}/chapter`;
    if (filters.subject_id) {
        api += `?subject_id=${filters.subject_id}`;
    }
    return http.get(api).then(response => {
        return response;
    }).catch(error => {
        console.error('Get Chapters API Error:', error);
        throw error;
    });
};

export const getChapter = async (chapterId) => {
    const api = `${API_BASE_URL}/chapter/${chapterId}`;
    return http.get(api).then(response => {
        return response;
    }).catch(error => {
        console.error('Get Chapter API Error:', error);
        throw error;
    });
};

export const getChaptersBySubject = async (subjectId) => {
    const api = `${API_BASE_URL}/chapter?subject_id=${subjectId}`;
    return http.get(api).then(response => {
        return response;
    }).catch(error => {
        console.error('Get Chapters by Subject API Error:', error);
        throw error;
    });
};

// Topic APIs
export const getAllTopics = async (filters = {}) => {
    let api = `${API_BASE_URL}/topic`;
    const params = new URLSearchParams();
    if (filters.subject_id) params.append('subject_id', filters.subject_id);
    if (filters.chapter_id) params.append('chapter_id', filters.chapter_id);
    
    if (params.toString()) {
        api += `?${params.toString()}`;
    }
    
    return http.get(api).then(response => {
        return response;
    }).catch(error => {
        console.error('Get Topics API Error:', error);
        throw error;
    });
};

export const getTopic = async (topicId) => {
    const api = `${API_BASE_URL}/topic/${topicId}`;
    return http.get(api).then(response => {
        return response;
    }).catch(error => {
        console.error('Get Topic API Error:', error);
        throw error;
    });
};

export const getTopicsByChapter = async (chapterId) => {
    const api = `${API_BASE_URL}/topic?chapter_id=${chapterId}`;
    return http.get(api).then(response => {
        return response;
    }).catch(error => {
        console.error('Get Topics by Chapter API Error:', error);
        throw error;
    });
};

// Quiz Builder APIs - Returns only subjects/chapters/topics that have questions
export const getQuizBuilderSubjects = async () => {
    const api = `${API_BASE_URL}/quiz-builder/subjects`;
    return http.get(api).then(response => {
        return response;
    }).catch(error => {
        console.error('Get Quiz Builder Subjects API Error:', error);
        throw error;
    });
};

export const getSubjectsForExam = async (exam_id) => {
    const api = `${API_BASE_URL}/exam/get_subjects_for_exam?exam_id=${exam_id}`;
    return http.get(api).then(response => {
        return response;
    }).catch(error => {
        console.error('Get Subjects for Exam API Error:', error);
        throw error;
    });
};

export const getQuizBuilderChapters = async (subjectId) => {
    const api = `${API_BASE_URL}/quiz-builder/chapters?subject_id=${subjectId}`;
    return http.get(api).then(response => {
        return response;
    }).catch(error => {
        console.error('Get Quiz Builder Chapters API Error:', error);
        throw error;
    });
};

export const getQuizBuilderTopics = async (chapterId) => {
    const api = `${API_BASE_URL}/quiz-builder/topics?chapter_id=${chapterId}`;
    return http.get(api).then(response => {
        return response;
    }).catch(error => {
        console.error('Get Quiz Builder Topics API Error:', error);
        throw error;
    });
};

// Question APIs
export const getAllQuestions = async (filters = {}) => {
    let api = `${API_BASE_URL}/question`;
    const params = new URLSearchParams();
    if (filters.subject_id) params.append('subject_id', filters.subject_id);
    if (filters.chapter_id) params.append('chapter_id', filters.chapter_id);
    if (filters.topic_id) params.append('topic_id', filters.topic_id);
    if (filters.exam_id) params.append('exam_id', filters.exam_id);
    if (filters.level) params.append('level', filters.level);
    
    if (params.toString()) {
        api += `?${params.toString()}`;
    }
    
    return http.get(api).then(response => {
        return response;
    }).catch(error => {
        console.error('Get Questions API Error:', error);
        throw error;
    });
};

export const getQuestion = async (questionId) => {
    const api = `${API_BASE_URL}/question/${questionId}`;
    return http.get(api).then(response => {
        return response;
    }).catch(error => {
        console.error('Get Question API Error:', error);
        throw error;
    });
};

export const getQuestionWithSolution = async (questionId) => {
    const api = `${API_BASE_URL}/question/${questionId}`;
    return http.get(api).then(response => {
        return response;
    }).catch(error => {
        console.error('Get Question with Solution API Error:', error);
        throw error;
    });
};

export const getQuestionsByTopic = async (topicId) => {
    const api = `${API_BASE_URL}/question?topic_id=${topicId}`;
    return http.get(api).then(response => {
        return response;
    }).catch(error => {
        console.error('Get Questions by Topic API Error:', error);
        throw error;
    });
};

// Exam APIs
export const getAllExams = async () => {
    const api = `${API_BASE_URL}/exam`;
    return http.get(api).then(response => {
        return response;
    }).catch(error => {
        console.error('Get Exams API Error:', error);
        throw error;
    });
};

export const getExam = async (examId) => {
    const api = `${API_BASE_URL}/exam/${examId}`;
    return http.get(api).then(response => {
        return response;
    }).catch(error => {
        console.error('Get Exam API Error:', error);
        throw error;
    });
};

// Quiz APIs
export const createQuiz = async (quizData) => {
    const api = `${API_BASE_URL}/quiz`;
    return http.post(api, quizData).then(response => {
        return response;
    }).catch(error => {
        console.error('Create Quiz API Error:', error);
        throw error;
    });
}; 

export const getQuiz = async (quizReference) => {
    const api = `${API_BASE_URL}/quiz/${quizReference}`;
    return http.get(api).then(response => {
        return response;
    }).catch(error => {
        console.error('Get Quiz API Error:', error);
        throw error;
    });
};

export const getQuestionsForQuiz = async (quizReference) => {
    const api = `${API_BASE_URL}/quiz/get_questions_for_quiz?quiz_reference=${quizReference}`;
    return http.get(api).then(response => {
        return response;
    }).catch(error => {
        console.error('Get Questions for Quiz API Error:', error);
        throw error;
    });
};

export const addUserAnswer = async (userAnswerData) => {
    const api = `${API_BASE_URL}/quiz/add_user_answer`;
    return http.post(api, userAnswerData).then(response => {
        return response;
    }).catch(error => {
        console.error('Add User Answer API Error:', error);
        throw error;
    });
};

export const skipQuestion = async (skipData) => {
    const api = `${API_BASE_URL}/quiz/skip_question`;
    return http.post(api, skipData).then(response => {
        return response;
    }).catch(error => {
        console.error('Skip Question API Error:', error);
        throw error;
    });
};

export const flagQuestion = async (questionId, flagReason = 'Invalid Question') => {
    const api = `${API_BASE_URL}/question/flag/${questionId}`;
    return http.post(api, { flag_reason: flagReason }).then(response => {
        return response;
    }).catch(error => {
        console.error('Flag Question API Error:', error);
        throw error;
    });
};

export const updateQuizStart = async (quizId, userId) => {
    const api = `${API_BASE_URL}/quiz/update_quiz_start`;
    const currentDateTime = new Date().toISOString();
    return http.post(api, { 
        quiz_id: quizId, 
        user_id: userId,
        start_date: currentDateTime 
    }).then(response => {
        return response;
    }).catch(error => {
        console.error('Update Quiz Start API Error:', error);
        throw error;
    });
};

export const submitQuizAnswers = async (quizId, answers) => {
    const api = `${API_BASE_URL}/quiz/${quizId}/submit`;
    return http.post(api, { answers }).then(response => {
        return response;
    }).catch(error => {
        console.error('Submit Quiz API Error:', error);
        throw error;
    });
};

export const submitQuiz = async (submitData) => {
    const api = `${API_BASE_URL}/quiz/submit_quiz`;
    return http.post(api, submitData).then(response => {
        return response;
    }).catch(error => {
        console.error('Submit Quiz API Error:', error);
        throw error;
    });
};

export const getQuizResults = async (quizId, userId) => {
    const api = `${API_BASE_URL}/quiz/${quizId}/results/${userId}`;
    return http.get(api).then(response => {
        return response;
    }).catch(error => {
        console.error('Get Quiz Results API Error:', error);
        throw error;
    });
};

export const getQuizResultsByReference = async (quizReference, userId) => {
    const api = `${API_BASE_URL}/quiz/results/${quizReference}`;
    return http.get(api).then(response => {
        return response;
    }).catch(error => {
        console.error('Get Quiz Results by Reference API Error:', error);
        throw error;
    });
};

// Performance Analysis APIs
export const getUserPerformanceAnalysis = async (userId, quizId) => {
    const api = `${API_BASE_URL}/user_performance/${userId}/${quizId}`;
    return http.get(api).then(response => {
        return response;
    }).catch(error => {
        console.error('Get User Performance Analysis API Error:', error);
        throw error;
    });
};

export const generateUserPerformanceAnalysis = async (userId, quizId) => {
    const api = `${API_BASE_URL}/user_performance/generate/${userId}/${quizId}`;
    return http.post(api, {}).then(response => {
        return response;
    }).catch(error => {
        console.error('Generate User Performance Analysis API Error:', error);
        throw error;
    });
};

// Performance Summary APIs
export const getLatestPerformanceSummary = async (userId) => {
    const api = `${API_BASE_URL}/user_performance_summary/latest/${userId}`;
    return http.get(api).then(response => {
        return response;
    }).catch(error => {
        console.error('Get Latest Performance Summary API Error:', error);
        throw error;
    });
};

export const getPerformanceSummaryHistory = async (userId, limit = 10, offset = 0) => {
    const api = `${API_BASE_URL}/user_performance_summary/history/${userId}?limit=${limit}&offset=${offset}`;
    return http.get(api).then(response => {
        return response;
    }).catch(error => {
        console.error('Get Performance Summary History API Error:', error);
        throw error;
    });
};

export const generatePerformanceSummary = async (userId, forceRegenerate = false, periodMonths = 6) => {
    const api = `${API_BASE_URL}/user_performance_summary/generate`;
    const payload = {
        user_id: userId,
        force_regenerate: forceRegenerate,
        period_months: periodMonths
    };
    return http.post(api, payload).then(response => {
        return response;
    }).catch(error => {
        console.error('Generate Performance Summary API Error:', error);
        throw error;
    });
};

export const getPerformanceData = async (userId) => {
    const api = `${API_BASE_URL}/user_performance_summary/performance_data/${userId}`;
    return http.get(api).then(response => {
        return response;
    }).catch(error => {
        console.error('Get Performance Data API Error:', error);
        throw error;
    });
};

export const getUserQuizHistory = async (userId, quizQuestionType = 'regular') => {
    const api = `${API_BASE_URL}/quiz/user_history/${userId}?quiz_question_type=${quizQuestionType}`;
    return http.get(api).then(response => {
        return response;
    }).catch(error => {
        console.error('Get User Quiz History API Error:', error);
        throw error;
    });
};

export const getUserStats = async (userId) => {
    const api = `${API_BASE_URL}/quiz/user_stats/${userId}`;
    return http.get(api).then(response => {
        return response;
    }).catch(error => {
        console.error('Get User Stats API Error:', error);
        throw error;
    });
};

export const getUnattemptedQuizzes = async () => {
    const api = `${API_BASE_URL}/quiz/unattempted`;
    return http.get(api).then(response => {
        return response;
    }).catch(error => {
        console.error('Get Unattempted Quizzes API Error:', error);
        throw error;
    });
};

// File Upload APIs
export const uploadFile = async (field, fileData) => {
    const formData = new FormData();
    formData.append(field, fileData.rawFile || fileData);
    
    return fetch(`${API_BASE_URL}/uploadFile`, {
        method: 'POST',
        body: formData,
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token') || localStorage.getItem('authToken')}`
        }
    }).then((response) => response.json())
        .then((responseJson) => { 
            return responseJson;
        })
        .catch((error) => {
            console.error('Upload File API Error:', error);
            throw error;
        });
};

export const uploadImportFile = async (field, fileData) => {
    const formData = new FormData();
    formData.append(field, fileData.rawFile || fileData);
    
    return fetch(`${API_BASE_URL}/fileimport`, {
        method: 'POST',
        body: formData,
        headers: {
            'Authorization': `Bearer ${localStorage.getItem('token') || localStorage.getItem('authToken')}`
        }
    }).then((response) => response.json())
        .then((responseJson) => { 
            return responseJson;
        })
        .catch((error) => {
            console.error('Upload Import File API Error:', error);
            throw error;
        });
};

// AI APIs
export const fetchSolution = async (imageUrl, suggestion = "") => {
    const api = `${AI_API_URL}solution_from_image_by_url`;
    const data = {
        image_url: imageUrl,
        suggestion: suggestion
    };
    return http.post(api, data).then((response) => {
        return response;
    }).catch(error => {
        console.error('Fetch Solution API Error:', error);
        throw error;
    });
};

export const updateQuestionSolution = async (questionId, solution = "") => {
    const api = `${API_BASE_URL}/question/solution/${questionId}`;
    const data = {
        solution: solution,
        id: questionId
    };
    return http.post(api, data).then((response) => {
        return response;
    }).catch(error => {
        console.error('Update Question Solution API Error:', error);
        throw error;
    });
};

// Generic CRUD APIs
export const createRecord = async (resource, data) => {
    const api = `${API_BASE_URL}/${resource}`;
    return http.post(api, data).then(response => {
        return response;
    }).catch(error => {
        console.error(`Create ${resource} API Error:`, error);
        throw error;
    });
};

export const updateRecord = async (resource, data) => {
    const api = `${API_BASE_URL}/${resource}/${data.id}`;
    return http.put(api, data).then(response => {
        return response;
    }).catch(error => {
        console.error(`Update ${resource} API Error:`, error);
        throw error;
    });
};

export const deleteRecord = async (resource, id) => {
    const api = `${API_BASE_URL}/${resource}/${id}`;
    return http.del(api).then(response => {
        return response;
    }).catch(error => {
        console.error(`Delete ${resource} API Error:`, error);
        throw error;
    });
};

export const getRecord = async (resource, id) => {
    const api = `${API_BASE_URL}/${resource}/${id}`;
    return http.get(api).then(response => {
        return response;
    }).catch(error => {
        console.error(`Get ${resource} API Error:`, error);
        throw error;
    });
};

export const getAllRecords = async (resource, filters = {}) => {
    let api = `${API_BASE_URL}/${resource}`;
    const params = new URLSearchParams(filters);
    
    if (params.toString()) {
        api += `?${params.toString()}`;
    }
    
    return http.get(api).then(response => {
        return response;
    }).catch(error => {
        console.error(`Get All ${resource} API Error:`, error);
        throw error;
    });
};

// Token Management
export const setAuthToken = (token) => {
    localStorage.setItem('authToken', token);
    localStorage.setItem('token', token); // For compatibility with http.js
    // Dispatch custom event to notify components of auth state change
    window.dispatchEvent(new CustomEvent('authStateChanged'));
};

export const getAuthToken = () => {
    return localStorage.getItem('authToken') || localStorage.getItem('token');
};

export const clearAuthToken = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    // Dispatch custom event to notify components of auth state change
    window.dispatchEvent(new CustomEvent('authStateChanged'));
};

// User Session Management
export const setUserSession = (userData) => {
    localStorage.setItem('user', JSON.stringify(userData));
    // Dispatch custom event to notify components of auth state change
    window.dispatchEvent(new CustomEvent('authStateChanged'));
};

export const getUserSession = () => {
    // Use JWT authentication service to get current user
    return jwtAuthService.getCurrentUser();
};

export const clearUserSession = () => {
    clearAuthToken();
    localStorage.removeItem('user');
};

// Quiz Generation API
export const generateCustomQuiz = async (quizConfig) => {
    const api = `${API_BASE_URL}/quiz/generate_custom_quiz`;
    
    // Prepare the payload
    const payload = {
        exam_id: quizConfig.exam,
        subject_ids: JSON.stringify(quizConfig.subjects),
        difficulty: quizConfig.difficulty,
        number_of_questions: quizConfig.numberOfQuestions,
        quiz_type: 'private'  // Custom quizzes are always private
    };

    // Add user_id if available
    const userSession = getUserSession();
    if (userSession && userSession.id) {
        payload.user_id = userSession.id;
    }

    // Add optional parameters only if they have values
    if (quizConfig.chapters && quizConfig.chapters.length > 0) {
        payload.chapter_ids = JSON.stringify(quizConfig.chapters);
    }
    
    if (quizConfig.topics && quizConfig.topics.length > 0) {
        payload.topic_ids = JSON.stringify(quizConfig.topics);
    }
    
    if (quizConfig.quiz_name) {
        payload.quiz_name = quizConfig.quiz_name;
    }
    
    if (quizConfig.quiz_description) {
        payload.quiz_description = quizConfig.quiz_description;
    }

    // Add question_type parameter (defaults to 'regular' if not specified)
    if (quizConfig.question_type) {
        payload.question_type = quizConfig.question_type;
    }

    console.log('Sending quiz generation payload:', payload);

    return http.post(api, payload).then(response => {
        console.log('Quiz generation response:', response);
        return response;
    }).catch(error => {
        console.error('Generate Custom Quiz API Error:', error);
        return {
            status: false,
            message: 'Failed to generate custom quiz',
            error: error
        };
    });
};

// API Response Helpers
export const isSuccessResponse = (response) => {
    return response && response.status === 'success';
};

export const getResponseData = (response) => {
    return response && response.result ? response.result : null;
};

export const getResponseMessage = (response) => {
    return response && response.message ? response.message : 'Unknown error occurred';
};

export const getErrorMessage = (error) => {
    if (error && error.message) {
        return error.message;
    } else if (typeof error === 'string') {
        return error;
    } else {
        return 'Network error occurred. Please try again.';
    }
};

// Student Report Card APIs
export const generateStudentReportCard = async (userId = null) => {
    const api = `${API_BASE_URL}/quiz/generate_student_report_card`;
    const data = userId ? { user_id: userId } : {};
    return http.post(api, data).then(response => {
        return response;
    }).catch(error => {
        console.error('Generate Student Report Card API Error:', error);
        throw error;
    });
};

export const getStudentReportCard = async (userId = null) => {
    const api = userId ? 
        `${API_BASE_URL}/quiz/get_student_report_card/${userId}` : 
        `${API_BASE_URL}/quiz/get_student_report_card`;
    return http.get(api).then(response => {
        return response;
    }).catch(error => {
        console.error('Get Student Report Card API Error:', error);
        throw error;
    });
};

export const getStudentBadges = async (userId = null) => {
    const api = userId ? 
        `${API_BASE_URL}/quiz/get_student_badges/${userId}` : 
        `${API_BASE_URL}/quiz/get_student_badges`;
    return http.get(api).then(response => {
        return response;
    }).catch(error => {
        console.error('Get Student Badges API Error:', error);
        throw error;
    });
};

// Topic Performance APIs
export const getUserTopicPerformance = async (userId = null) => {
    const api = userId ? 
        `${API_BASE_URL}/quiz/user_topic_performance/${userId}` : 
        `${API_BASE_URL}/quiz/user_topic_performance`;
    return http.get(api).then(response => {
        return response;
    }).catch(error => {
        console.error('Get User Topic Performance API Error:', error);
        throw error;
    });
};

export const getUserTopicSummary = async (userId = null) => {
    const api = userId ? 
        `${API_BASE_URL}/quiz/user_topic_summary/${userId}` : 
        `${API_BASE_URL}/quiz/user_topic_summary`;
    return http.get(api).then(response => {
        return response;
    }).catch(error => {
        console.error('Get User Topic Summary API Error:', error);
        throw error;
    });
};

// Quiz Performance APIs
export const getQuizWisePerformance = async () => {
    const api = `${API_BASE_URL}/report_card/quiz_wise_performance`;
    return http.get(api).then(response => {
        return response;
    }).catch(error => {
        console.error('Get Quiz Wise Performance API Error:', error);
        throw error;
    });
};

export const getSubjectChapterPerformance = async () => {
    const api = `${API_BASE_URL}/report_card/subject_chapter_performance`;
    return http.get(api).then(response => {
        return response;
    }).catch(error => {
        console.error('Get Subject Chapter Performance API Error:', error);
        throw error;
    });
};

// ==================== POINT SYSTEM APIs ====================

/**
 * Get user point summary including current points, weekly status, AI tutor minutes, and achievements
 */
export const getUserPointSummary = async (userId) => {
    const api = `${API_BASE_URL}/user_points/summary/${userId}`;
    return http.get(api).then(response => {
        return response;
    }).catch(error => {
        console.error('Get User Point Summary API Error:', error);
        throw error;
    });
};

/**
 * Get user point transaction history with pagination
 */
export const getUserPointTransactions = async (userId, options = {}) => {
    const { limit = 5, offset = 0, transactionType = null } = options;
    let api = `${API_BASE_URL}/user_points/transactions/${userId}?limit=${limit}&offset=${offset}`;
    
    if (transactionType) {
        api += `&transaction_type=${transactionType}`;
    }
    
    return http.get(api).then(response => {
        return response;
    }).catch(error => {
        console.error('Get User Point Transactions API Error:', error);
        throw error;
    });
};

/**
 * Get points leaderboard
 */
export const getPointsLeaderboard = async (options = {}) => {
    const { period = 'all_time', limit = 5 } = options;
    const api = `${API_BASE_URL}/user_points/leaderboard?period=${period}&limit=${limit}`;
    
    return http.get(api).then(response => {
        return response;
    }).catch(error => {
        console.error('Get Points Leaderboard API Error:', error);
        throw error;
    });
};

/**
 * Start AI tutor session
 */
export const startAITutorSession = async (userId, minutesRequested) => {
    const api = `${API_BASE_URL}/user_points/ai_tutor/start`;
    const data = {
        user_id: userId,
        minutes_requested: minutesRequested
    };
    
    return http.post(api, data).then(response => {
        return response;
    }).catch(error => {
        console.error('Start AI Tutor Session API Error:', error);
        throw error;
    });
};

/**
 * End AI tutor session
 */
export const endAITutorSession = async (sessionId, minutesUsed, conversationMessages = 0) => {
    const api = `${API_BASE_URL}/user_points/ai_tutor/end`;
    const data = {
        session_id: sessionId,
        minutes_used: minutesUsed,
        conversation_messages: conversationMessages
    };
    
    return http.post(api, data).then(response => {
        return response;
    }).catch(error => {
        console.error('End AI Tutor Session API Error:', error);
        throw error;
    });
};

/**
 * Debug endpoint to manually trigger point generation for a quiz
 * Useful for testing and debugging point calculation issues
 */
export const debugQuizPoints = async (quizId, userId = null, force = false) => {
    const api = `${API_BASE_URL}/quiz/debug_points`;
    const data = {
        quiz_id: quizId,
        force: force
    };
    
    if (userId) {
        data.user_id = userId;
    }
    
    return http.post(api, data).then(response => {
        return response;
    }).catch(error => {
        console.error('Debug Quiz Points API Error:', error);
        throw error;
    });
};

// ===========================
// Razorpay Payment APIs
// ===========================

/**
 * Create a Razorpay order for subscription payment
 * @param {string} planId - The subscription plan ID
 * @param {string} billingCycle - Either 'monthly' or 'academic'
 * @returns {Promise} Response with order details (order_id, amount, key_id, etc.)
 */
export const createRazorpayOrder = async (planId, billingCycle) => {
    const api = `${API_BASE_URL}/subscription/create-order`;
    const data = {
        plan_id: planId,
        billing_cycle: billingCycle
    };
    
    return http.post(api, data).then(response => {
        return response;
    }).catch(error => {
        console.error('Create Razorpay Order API Error:', error);
        throw error;
    });
};

/**
 * Verify Razorpay payment after successful checkout
 * @param {string} razorpayOrderId - Razorpay order ID
 * @param {string} razorpayPaymentId - Razorpay payment ID
 * @param {string} razorpaySignature - Razorpay signature for verification
 * @returns {Promise} Response with verification status and subscription details
 */
export const verifyRazorpayPayment = async (razorpayOrderId, razorpayPaymentId, razorpaySignature) => {
    const api = `${API_BASE_URL}/subscription/verify-payment`;
    const data = {
        razorpay_order_id: razorpayOrderId,
        razorpay_payment_id: razorpayPaymentId,
        razorpay_signature: razorpaySignature
    };
    
    return http.post(api, data).then(response => {
        return response;
    }).catch(error => {
        console.error('Verify Razorpay Payment API Error:', error);
        throw error;
    });
};

// ===========================
// Account Management APIs
// ===========================

/**
 * Request account deletion (soft delete with 30-day grace period)
 * @param {string} password - User's current password for verification
 * @param {string} reason - Optional reason for deletion
 * @returns {Promise} Response with deletion request status
 */
export const deleteUserAccount = async (password, reason = null) => {
    const api = `${API_BASE_URL}/user/delete-account`;
    const data = {
        password: password,
        reason: reason
    };
    
    return http.del(api, data).then(response => {
        // If deletion successful, logout user
        if (response.status === 'success') {
            jwtAuthService.logout();
        }
        return response;
    }).catch(error => {
        console.error('Delete Account API Error:', error);
        throw error;
    });
};

// ===========================
// Mobile Verification APIs
// ===========================

/**
 * Send OTP to mobile number for verification
 * @param {string} mobileNumber - 10-digit Indian mobile number
 * @returns {Promise} Response with OTP sent status, expiry time, and attempts remaining
 */
export const sendMobileOTP = async (mobileNumber) => {
    const api = `${API_BASE_URL}/mobile_verification/send_otp`;
    const data = {
        mobile_number: mobileNumber
    };
    
    return http.post(api, data).then(response => {
        return response;
    }).catch(error => {
        console.error('Send Mobile OTP API Error:', error);
        throw error;
    });
};

/**
 * Verify OTP code for mobile number
 * @param {string} mobileNumber - 10-digit Indian mobile number
 * @param {string} otpCode - 6-digit OTP code
 * @returns {Promise} Response with verification status
 */
export const verifyMobileOTP = async (mobileNumber, otpCode) => {
    const api = `${API_BASE_URL}/mobile_verification/verify_otp`;
    const data = {
        mobile_number: mobileNumber,
        otp_code: otpCode
    };
    
    return http.post(api, data).then(response => {
        return response;
    }).catch(error => {
        console.error('Verify Mobile OTP API Error:', error);
        throw error;
    });
};

/**
 * Resend OTP to mobile number
 * @param {string} mobileNumber - 10-digit Indian mobile number
 * @returns {Promise} Response with new OTP sent status, expiry time, and attempts remaining
 */
export const resendMobileOTP = async (mobileNumber) => {
    const api = `${API_BASE_URL}/mobile_verification/resend_otp`;
    const data = {
        mobile_number: mobileNumber
    };
    
    return http.post(api, data).then(response => {
        return response;
    }).catch(error => {
        console.error('Resend Mobile OTP API Error:', error);
        throw error;
    });
};

/**
 * Get mobile verification status for current user
 * @returns {Promise} Response with mobile verification status and details
 */
export const getMobileVerificationStatus = async () => {
    const api = `${API_BASE_URL}/mobile_verification/status`;
    
    return http.get(api).then(response => {
        return response;
    }).catch(error => {
        console.error('Get Mobile Verification Status API Error:', error);
        throw error;
    });
};

